import { useQuery } from '@apollo/client';
import { APP_QUERY_PARAMS } from 'utils/queryParam.utils';
import { SUPPLY_CHAINS_PER_PAGE } from 'utils/pagination.utils';
import { GET_PRODUCTS_AND_BASE_CHAIN } from 'graphql/secure/queries/product.queries';
import { useMemo } from 'react';
import { IProduct } from 'FeatureModules/ProductPage/types/product.types';
import { IGraphQlConnection } from 'types/core.types';
import { StringParam, useQueryParams, withDefault } from 'use-query-params';
import { removeGraphConnections } from 'utils/graphql.utils';
import { TSortOrder } from 'types/pagination.types';

interface IUseSupplyChains {
  isArchived?: boolean;
}

const useSupplyChains = ({ isArchived }: IUseSupplyChains = {}) => {
  const [query, setQuery] = useQueryParams({
    [APP_QUERY_PARAMS.order]: withDefault(StringParam, 'DESC'),
    [APP_QUERY_PARAMS.orderBy]: withDefault(StringParam, ''),
    [APP_QUERY_PARAMS.searchTerm]: withDefault(StringParam, ''),
  });

  const searchTerm = query[APP_QUERY_PARAMS.searchTerm];
  const orderByMethod = query[APP_QUERY_PARAMS.order] as TSortOrder | undefined;
  const orderByField = query[APP_QUERY_PARAMS.orderBy];

  const handleSearchTermChange = (searchTerm: string) => {
    setQuery({ [APP_QUERY_PARAMS.searchTerm]: searchTerm });
  };

  const {
    data: productsData,
    loading,
    error,
    fetchMore,
  } = useQuery<{
    products: IGraphQlConnection<IProduct>;
  }>(GET_PRODUCTS_AND_BASE_CHAIN, {
    variables: {
      filters: {
        searchTerm: searchTerm?.length ? searchTerm : undefined,
        status: isArchived ? ['ARCHIVED'] : ['DRAFT', 'PUBLISHED'],
      },
      first: SUPPLY_CHAINS_PER_PAGE,
      orderBy: {
        orderByMethod,
        orderByField: orderByField.length ? orderByField : undefined,
      },
    },
  });

  const hasNextPage: boolean = !!productsData?.products?.pageInfo?.hasNextPage;
  const endCursor = productsData?.products?.pageInfo?.endCursor;

  const handlePageEndReached = () => {
    if (endCursor) {
      fetchMore({
        variables: {
          after: endCursor,
        },
      });
    }
  };

  const productsWithSupplyChains = useMemo(
    () => (productsData?.products ? removeGraphConnections(productsData.products) : []),
    [productsData]
  );

  return {
    searchTerm,
    productsWithSupplyChains,
    loading,
    error,
    hasNextPage,
    endCursor,
    setSearchTerm: handleSearchTermChange,
    handlePageEndReached,
  };
};

export default useSupplyChains;
